import React from "react";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";
import { PValueMatrix as PValueMatrixCompute } from "../util/pValueMatrix";

const HEADER_BG = "#374151";

const CARD     = { borderRadius: "8px", marginTop: "16px", width: "fit-content", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" };

const TH = {
    background: HEADER_BG, color: "white",
    padding: "8px 14px", fontWeight: "600", fontSize: "13px",
    textAlign: "center", whiteSpace: "nowrap"
};
const CELL = {
    padding: "8px 16px", fontSize: "13px",
    textAlign: "center", verticalAlign: "middle",
    borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb",
    minWidth: "90px"
};

function sigStars(p) {
    if (p < 0.001) return "***";
    if (p < 0.01)  return "**";
    if (p < 0.05)  return "*";
    return "";
}

function pCellStyle(val) {
    if (val === null || isNaN(val)) return {};
    if (val < 0.001) return { background: "#bbf7d0", color: "#14532d", fontWeight: "700" };
    if (val < 0.01)  return { background: "#d1fae5", color: "#166534", fontWeight: "600" };
    if (val < 0.05)  return { background: "#ecfdf5", color: "#166534" };
    return { background: "white", color: "#4b5563" };
}

function pTooltip(rowM, colM, val) {
    if (val === null || isNaN(val)) return "";
    return val < 0.05
        ? `Significant difference between ${rowM} and ${colM} (p = ${val.toFixed(3)})`
        : `No significant difference between ${rowM} and ${colM} (p = ${val.toFixed(3)})`;
}

export default function PValueMatrix({ data, modelType }) {
    if (!data || !data.twoAfcData) return null;

    const modelTag    = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";
    const transformed = matrixTransformation(data);
    const methods     = transformed.sortedMethods || [];
    const model       = modelType === "BT" ? bradleyTerry(transformed) : thurstoneMosteller(transformed);
    const pMatrix     = PValueMatrixCompute(transformed.matrix);

    const ranked = [...methods.map((m, i) => ({ m, i }))]
        .sort((a, b) => model.betas[b.i] - model.betas[a.i]);
    const orderedMethods  = ranked.map(r => r.m);
    const orderedOrigIdx  = ranked.map(r => r.i);

    const sigCount = methods.reduce((acc, _, i) =>
        acc + methods.reduce((a, __, j) => {
            if (i === j) return a;
            const v = pMatrix[i][j];
            return a + (v !== null && !isNaN(v) && v < 0.05 ? 1 : 0);
        }, 0), 0) / 2;

    return (
        <div style={CARD}>

            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>P‑value Matrix</h4>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
                {sigCount > 0 && (
                    <span style={{ marginLeft: "auto", background: "#16a34a", color: "white", fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "10px" }}>
                        {sigCount} significant pair{sigCount !== 1 ? "s" : ""}
                    </span>
                )}
                {sigCount === 0 && (
                    <span style={{ marginLeft: "auto", color: "#9ca3af", fontSize: "10px" }}>no significant pairs</span>
                )}
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                Pairwise significance of differences between methods.
            </div>

            {/* matrix */}
            <div style={{ ...SECTION, background: "white", overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr>
                            <th style={{ ...TH, borderRight: "2px solid #1f2937", borderBottom: "2px solid #1f2937" }} />
                            {orderedMethods.map((m, ri) => (
                                <th key={ri} style={{
                                    ...TH,
                                    borderRight: ri < orderedMethods.length - 1 ? "1px solid #4b5563" : "none",
                                    borderBottom: "2px solid #1f2937"
                                }}>
                                    {m}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {orderedMethods.map((rowMethod, ri) => {
                            const origI = orderedOrigIdx[ri];
                            return (
                            <tr key={ri}>
                                <th style={{
                                    ...TH,
                                    borderRight: "2px solid #1f2937",
                                    borderBottom: ri < orderedMethods.length - 1 ? "1px solid #4b5563" : "none"
                                }}>
                                    {rowMethod}
                                </th>
                                {orderedMethods.map((colMethod, ci) => {
                                    const origJ = orderedOrigIdx[ci];
                                    if (origI === origJ) return (
                                        <td key={ci} style={{ ...CELL, background: "#f1f5f9", color: "#94a3b8", fontWeight: "400", fontSize: "16px" }}>
                                            -
                                        </td>
                                    );
                                    const val     = pMatrix[origI][origJ];
                                    const display = val === null || isNaN(val) ? "-"
                                        : val < 0.001 ? "< 0.001"
                                        : val.toFixed(3);
                                    const stars   = val !== null && !isNaN(val) ? sigStars(val) : "";
                                    return (
                                        <td
                                            key={ci}
                                            title={pTooltip(rowMethod, colMethod, val)}
                                            style={{ ...CELL, ...pCellStyle(val), cursor: val !== null && !isNaN(val) ? "help" : "default" }}
                                        >
                                            {display}
                                            {stars && (
                                                <sup style={{ fontSize: "10px", marginLeft: "2px", fontWeight: "700" }}>{stars}</sup>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={FOOTER}>
                <span>Hover cells for details</span>
                <span>|</span>
                <span style={{ background: "#bbf7d0", color: "#14532d", padding: "1px 5px", borderRadius: "3px", fontWeight: "700" }}>dark green</span>
                <span>p &lt; 0.001 ***</span>
                <span>|</span>
                <span style={{ background: "#d1fae5", color: "#166534", padding: "1px 5px", borderRadius: "3px", fontWeight: "600" }}>green</span>
                <span>p &lt; 0.01 **</span>
                <span>|</span>
                <span style={{ background: "#ecfdf5", color: "#166534", padding: "1px 5px", borderRadius: "3px" }}>light green</span>
                <span>p &lt; 0.05 *</span>
            </div>
        </div>
    );
}
